import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { Images } from "../../../utils/Images";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchMonthlyDonationsAction } from "../../../store/slices/monthlyDonationSlice";
import { fetchCmsHomeAction } from "../../../store/slices/cmsSlice";
import { validateDonationAmount } from "../../../utils/donationValidation";

const SafeLifeEveryMonth = ({
  isMediumScreen = false,
  isSmallScreen = false,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery("(max-width: 600px)");

  const [selectedAmount, setSelectedAmount] = useState(3000);
  const [customAmount, setCustomAmount] = useState("3,000");
  const [selectedCause, setSelectedCause] = useState("");
  const causeSelectRef = useRef(null);
  const [causeMenuWidth, setCauseMenuWidth] = useState(null);

  const dispatch = useDispatch();
  const cmsHome = useSelector((state) => state.cms.home);
  const { list: monthlyDonations, loading: monthlyDonationsLoading } = useSelector((state) => state.monthlyDonation);

  // Fetch CMS for minimum amount validation
  useEffect(() => {
    if (!cmsHome) dispatch(fetchCmsHomeAction());
  }, [cmsHome, dispatch]);

  // Fetch monthly donations on component mount
  useEffect(() => {
    if (monthlyDonations.length === 0 && !monthlyDonationsLoading) {
      dispatch(fetchMonthlyDonationsAction());
    }
  }, [dispatch, monthlyDonations.length, monthlyDonationsLoading]);

  const navigate = useNavigate();
  const handleDonateClick = () => {
    if (!selectedCause) {
      alert("Please select a cause");
      return;
    }

    const validation = validateDonationAmount(selectedAmount, cmsHome, false, "₹");
    if (!validation.isValid) {
      alert(validation.error);
      return;
    }

    const selectedDonation = monthlyDonations.find(donation => String(donation.id) === String(selectedCause));

    if (selectedDonation) {
      const params = new URLSearchParams({
        id: selectedDonation.id.toString(),
        title: selectedDonation.title || selectedDonation.originalData?.title || '',
        amount: selectedAmount.toString(),
      });
      navigate(`/donate-monthly-page?${params.toString()}`);
    } else {
      navigate("/donate-monthly-page");
    }
  };

  const amountOptions = [3000, 6000, 10000, 12000, 14000];

  // Responsive styles function
  const getResponsiveStyles = () => {
    if (isMobile) {
      return {
        container: {
          paddingTop: "16px",
          paddingRight: "10px",
          paddingBottom: "16px",
          paddingLeft: "10px",
        },
        titleIcon: {
          width: "14px",
          height: "14px",
        },
        titleText: {
          fontSize: "14px",
          lineHeight: "11.19px",
          letterSpacing: "-2%",
          marginBottom: "16px",
        },
        selectCauseText: {
          fontSize: "12px",
          lineHeight: "100%",
          letterSpacing: "0%",
        },
        dropdown: {
          height: "38.437965393066406px",
          borderRadius: "4.99px",
          paddingTop: "8px",
          paddingRight: "9.97px",
          paddingBottom: "8px",
          paddingLeft: "9.97px",
          borderWidth: "0.62px",
          marginBottom: "11px",
        },
        buttonBox: {
          gap: "3px",
          marginBottom: "11px",
        },
        amountButton: {
          width: "57.9px",
          minWidth: "57.9px !important",
          height: "30.437963485717773px",
          borderRadius: "4.99px",
          paddingTop: "4px",
          paddingRight: "14.96px",
          paddingBottom: "4px",
          paddingLeft: "14.96px",
          borderWidth: "0.62px",
        },
        amountButtonText: {
          fontFamily: "Red Hat Display",
          fontWeight: 800,
          fontSize: "10px",
          lineHeight: "13.46px",
          letterSpacing: "0%",
        },
        customAmountInput: {
          height: "38.437965393066406px",
          borderRadius: "4.99px",
          paddingTop: "8px",
          paddingRight: "9.97px",
          paddingBottom: "8px",
          paddingLeft: "9.97px",
          borderWidth: "0.62px",
          marginBottom: "11px",
        },
        currencyIcon: {
          width: "16px",
          height: "14px",
        },
        customAmountText: {
          fontWeight: 700,
          fontSize: "14px",
          lineHeight: "9.97px",
          letterSpacing: "0%",
        },
        donateButton: {
          height: "37px",
          minWidth: "0 !important",
          borderRadius: "6.23px",
          paddingTop: "14px",
          paddingBottom: "14px",
          boxShadow: "0px 2.49px 0px 0px #141414",
        },
        donateButtonText: {
          fontWeight: 700,
          fontSize: "12px",
          lineHeight: "8.73px",
          letterSpacing: "0%",
        },
      };
    } else if (isMediumScreen) {
      return {
        container: {
          paddingTop: "30px",
          paddingRight: "12px",
          paddingBottom: "30px",
          paddingLeft: "12px",
        },
        titleIcon: {
          width: "24px",
          height: "24px",
        },
        titleText: {
          fontSize: "24px",
          lineHeight: "20px",
          letterSpacing: "-2%",
          marginBottom: "30px",
        },
        selectCauseText: {
          fontSize: "16px",
          lineHeight: "100%",
          letterSpacing: "0%",
        },
        dropdown: {
          height: "55.676856994628906px",
          borderRadius: "8.91px",
          paddingTop: "7.79px",
          paddingRight: "17.82px",
          paddingBottom: "7.79px",
          paddingLeft: "17.82px",
          borderWidth: "1.11px",
          marginBottom: "20px",
        },
        buttonBox: {
          gap: "6.14px",
          marginBottom: "20px",
        },
        amountButton: {
          width: "85px",
          minWidth: "85px !important",
          height: "48.99563217163086px",
          borderRadius: "8.91px",
          paddingTop: "4.45px",
          paddingRight: "26.72px",
          paddingBottom: "4.45px",
          paddingLeft: "26.72px",
          borderWidth: "1.11px",
        },
        amountButtonText: {
          fontFamily: "Red Hat Display",
          fontWeight: 800,
          fontSize: "14px",
          lineHeight: "24.05px",
          letterSpacing: "0%",
        },
        customAmountInput: {
          height: "53.667335510253906px",
          borderRadius: "8.91px",
          paddingTop: "6.79px",
          paddingRight: "17.82px",
          paddingBottom: "6.79px",
          paddingLeft: "17.82px",
          borderWidth: "1.11px",
          marginBottom: "24px",
        },
        currencyIcon: {
          width: "17.82px",
          height: "17.82px",
        },
        customAmountText: {
          fontWeight: 700,
          fontSize: "17.82px",
          lineHeight: "17.82px",
          letterSpacing: "0%",
        },
        donateButton: {
          height: "auto",
          minWidth: "0 !important",
          borderRadius: "11.14px",
          paddingTop: "21.16px",
          paddingRight: "33.41px",
          paddingBottom: "21.16px",
          paddingLeft: "33.41px",
          boxShadow: "0px 4.45px 0px 0px #141414",
        },
        donateButtonText: {
          fontWeight: 700,
          fontSize: "16px",
          lineHeight: "auto",
          letterSpacing: "0%",
        },
      };
    } else if (isSmallScreen) {
      return {
        container: {
          paddingTop: "24px",
          paddingRight: "16px",
          paddingBottom: "24px",
          paddingLeft: "16px",
        },
        titleIcon: {
          width: "20px",
          height: "20px",
        },
        titleText: {
          fontSize: "20px",
          lineHeight: "18px",
          letterSpacing: "-2%",
          marginBottom: "24px",
        },
        selectCauseText: {
          fontSize: "14px",
          lineHeight: "100%",
          letterSpacing: "0%",
        },
        dropdown: {
          height: "48px",
          borderRadius: "7px",
          paddingTop: "6px",
          paddingRight: "14px",
          paddingBottom: "6px",
          paddingLeft: "14px",
          borderWidth: "1px",
          marginBottom: "16px",
        },
        buttonBox: {
          gap: "8px",
          marginBottom: "16px",
        },
        amountButton: {
          width: "90px",
          minWidth: "90px !important",
          height: "42px",
          borderRadius: "7px",
          paddingTop: "4px",
          paddingRight: "20px",
          paddingBottom: "4px",
          paddingLeft: "20px",
          borderWidth: "1px",
        },
        amountButtonText: {
          fontFamily: "Red Hat Display",
          fontWeight: 800,
          fontSize: "12px",
          lineHeight: "20px",
          letterSpacing: "0%",
        },
        customAmountInput: {
          height: "48px",
          borderRadius: "7px",
          paddingTop: "6px",
          paddingRight: "14px",
          paddingBottom: "6px",
          paddingLeft: "14px",
          borderWidth: "1px",
          marginBottom: "20px",
        },
        currencyIcon: {
          width: "16px",
          height: "16px",
        },
        customAmountText: {
          fontWeight: 700,
          fontSize: "16px",
          lineHeight: "16px",
          letterSpacing: "0%",
        },
        donateButton: {
          height: "auto",
          minWidth: "0 !important",
          borderRadius: "9px",
          paddingTop: "18px",
          paddingRight: "28px",
          paddingBottom: "18px",
          paddingLeft: "28px",
          boxShadow: "0px 3.5px 0px 0px #141414",
        },
        donateButtonText: {
          fontWeight: 700,
          fontSize: "14px",
          lineHeight: "auto",
          letterSpacing: "0%",
        },
      };
    } else {
      // Desktop styles - same as isSmallScreen for now, but can be customized
      return {
        container: {
          paddingTop: "30px",
          paddingRight: "17px",
          paddingBottom: "30px",
          paddingLeft: "17px",
        },
        titleIcon: {
          width: "24px",
          height: "24px",
        },
        titleText: {
          fontSize: "24px",
          lineHeight: "20px",
          letterSpacing: "-2%",
          marginBottom: "30px",
        },
        selectCauseText: {
          fontSize: "16px",
          lineHeight: "100%",
          letterSpacing: "0%",
        },
        dropdown: {
          height: "55.676856994628906px",
          borderRadius: "8.91px",
          paddingTop: "7.79px",
          paddingRight: "17.82px",
          paddingBottom: "7.79px",
          paddingLeft: "17.82px",
          borderWidth: "1.11px",
          marginBottom: "20px",
        },
        buttonBox: {
          gap: "9.14px",
          marginBottom: "20px",
        },
        amountButton: {
          width: "102.4454116821289px",
          minWidth: "102.4454116821289px !important",
          height: "48.99563217163086px",
          borderRadius: "8.91px",
          paddingTop: "4.45px",
          paddingRight: "26.72px",
          paddingBottom: "4.45px",
          paddingLeft: "26.72px",
          borderWidth: "1.11px",
        },
        amountButtonText: {
          fontFamily: "Red Hat Display",
          fontWeight: 800,
          fontSize: "14px",
          lineHeight: "24.05px",
          letterSpacing: "0%",
        },
        customAmountInput: {
          height: "53.667335510253906px",
          borderRadius: "8.91px",
          paddingTop: "6.79px",
          paddingRight: "17.82px",
          paddingBottom: "6.79px",
          paddingLeft: "17.82px",
          borderWidth: "1.11px",
          marginBottom: "24px",
        },
        currencyIcon: {
          width: "17.82px",
          height: "17.82px",
        },
        customAmountText: {
          fontWeight: 700,
          fontSize: "17.82px",
          lineHeight: "17.82px",
          letterSpacing: "0%",
        },
        donateButton: {
          height: "auto",
          minWidth: "0 !important",
          borderRadius: "11.14px",
          paddingTop: "21.16px",
          paddingRight: "33.41px",
          paddingBottom: "21.16px",
          paddingLeft: "33.41px",
          boxShadow: "0px 4.45px 0px 0px #141414",
        },
        donateButtonText: {
          fontWeight: 700,
          fontSize: "16px",
          lineHeight: "auto",
          letterSpacing: "0%",
        },
      };
    }
  };

  const styles = getResponsiveStyles();
  const wrapCauseMenuOptions = isMobile || isSmallScreen;

  const getCauseLabel = (donation) =>
    donation?.title || donation?.originalData?.title || "Monthly Donation";

  const causeMenuItemTextSx = {
    fontWeight: 500,
    fontSize: wrapCauseMenuOptions ? "12px" : "14px",
    lineHeight: 1.4,
    letterSpacing: "0%",
    color: "#1D1D1B",
    whiteSpace: "normal",
    wordBreak: "break-word",
    overflowWrap: "anywhere",
    width: "100%",
    maxWidth: "100%",
    boxSizing: "border-box",
    display: "-webkit-box",
    WebkitLineClamp: 3,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
  };

  const causeSelectedTextSx = {
    fontWeight: 500,
    fontSize: wrapCauseMenuOptions ? "12px" : "14px",
    lineHeight: wrapCauseMenuOptions ? "12px" : "14px",
    letterSpacing: "0%",
    color: "#1D1D1B",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    minWidth: 0,
    flex: 1,
    width: "100%",
    boxSizing: "border-box",
  };

  const renderSelectedCause = (value) => {
    if (!value) {
      return (
        <Box component="span" sx={{ ...causeSelectedTextSx, color: "#979796" }}>
          Select Cause
        </Box>
      );
    }
    const donation = monthlyDonations.find((d) => String(d.id) === String(value));
    const label = donation ? getCauseLabel(donation) : "";
    return (
      <Box component="span" sx={causeSelectedTextSx} title={label}>
        {label}
      </Box>
    );
  };

  const handleCauseMenuOpen = () => {
    if (causeSelectRef.current) {
      setCauseMenuWidth(causeSelectRef.current.clientWidth);
    }
  };

  const handleCauseMenuClose = () => {
    setCauseMenuWidth(null);
  };

  const causeMenuProps = wrapCauseMenuOptions
    ? {
        autoWidth: false,
        disableAutoFocusItem: true,
        anchorOrigin: { vertical: "bottom", horizontal: "left" },
        transformOrigin: { vertical: "top", horizontal: "left" },
        PaperProps: {
          sx: {
            width: causeMenuWidth ?? undefined,
            maxWidth: causeMenuWidth ?? "100%",
            minWidth: causeMenuWidth ?? undefined,
            boxSizing: "border-box",
            overflow: "hidden",
          },
        },
        MenuListProps: {
          sx: {
            width: "100%",
            maxWidth: "100%",
            py: 0,
          },
        },
      }
    : undefined;

  const handleAmountSelect = (amount) => {
    setSelectedAmount(amount);
    setCustomAmount(amount.toLocaleString("en-IN"));
  };

  const handleCustomAmountChange = (e) => {
    const value = e.target.value.replace(/[^\d]/g, "");
    if (value) {
      const numValue = parseInt(value);
      setCustomAmount(numValue.toLocaleString("en-IN"));
      setSelectedAmount(numValue);
    } else {
      setCustomAmount("");
      setSelectedAmount(0);
    }
  };

  return (
    <Box
      sx={{
        width: isSmallScreen ? "100%" : isMediumScreen ? "480px" : "586px",
        // height: '416.5625305175781px',
        borderRadius: isMobile ? "11px" : "20px",
        paddingTop: styles.container.paddingTop,
        paddingRight: styles.container.paddingRight,
        paddingBottom: styles.container.paddingBottom,
        paddingLeft: styles.container.paddingLeft,
        border: "1px solid #E8E8E8",
        background: "#FFFFFF",
        display: "flex",
        flexDirection: "column",
        // gap: '10px',
      }}
    >
      {/* Title with Icon */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "12px",
          marginBottom: styles.titleText.marginBottom,
        }}
      >
        <img
          src={Images.streamlineSharpStar}
          alt="Star icon"
          style={{
            width: styles.titleIcon.width,
            height: styles.titleIcon.height,
            objectFit: "contain",
          }}
        />
        <Typography
          sx={{
            fontWeight: 800,
            fontSize: styles.titleText.fontSize,
            lineHeight: styles.titleText.lineHeight,
            letterSpacing: styles.titleText.letterSpacing,
            verticalAlign: "middle",
            color: "#1D1D1B",
          }}
        >
          Save A Life Every Month
        </Typography>
      </Box>

      {/* Select Cause */}
      <Typography
        sx={{
          fontWeight: 600,
          fontSize: styles.selectCauseText.fontSize,
          lineHeight: styles.selectCauseText.lineHeight,
          letterSpacing: styles.selectCauseText.letterSpacing,
          color: "#141414",
          marginBottom: "8px",
        }}
      >
        Select Cause
      </Typography>

      <FormControl
        fullWidth
        sx={{
          marginBottom: styles.dropdown.marginBottom,
        }}
      >
        <Select
          ref={causeSelectRef}
          value={selectedCause}
          onChange={(e) => setSelectedCause(e.target.value)}
          displayEmpty
          renderValue={renderSelectedCause}
          onOpen={wrapCauseMenuOptions ? handleCauseMenuOpen : undefined}
          onClose={wrapCauseMenuOptions ? handleCauseMenuClose : undefined}
          MenuProps={causeMenuProps}
          IconComponent={() => (
            <Box
              component="img"
              src={Images.dropdownArrow} 
              alt="dropdown"
              sx={{
                position: "absolute",
                zIndex: -1,
                right: isMobile ? 10 : 16,
                top: "50%",
                transform: "translateY(-50%)",
                width:isMobile?"12px": 22,
                height: isMobile?"12px": 22,
                transition: "transform 0.2s",
                "&.MuiSelect-iconOpen": {
                  transform: "translateY(-50%) rotate(180deg)",
                },
              }}
            />
          )}
          sx={{
            height: styles.dropdown.height,
            borderRadius: styles.dropdown.borderRadius,
            paddingTop: 0,
            paddingBottom: 0,
            display: "flex",
            alignItems: "center",
            zIndex: 1,
            border: `${styles.dropdown.borderWidth} solid #DDE2E5`,
            "& .MuiOutlinedInput-notchedOutline": {
              border: "none",
            },
            "& .MuiSelect-icon": {
              display: "none",
            },
            "& .MuiSelect-select": {
              display: "flex !important",
              alignItems: "center !important",
              height: "100% !important",
              minHeight: "100% !important",
              py: "0 !important",
              pl: `${styles.dropdown.paddingLeft} !important`,
              pr: isMobile ? "22px !important" : "32px !important",
              overflow: "hidden !important",
              textOverflow: "ellipsis !important",
              whiteSpace: "nowrap !important",
              boxSizing: "border-box",
            },
          }}
        >
          <MenuItem value="" disabled>
            <Typography
              sx={{
                fontWeight: 500,
                fontSize: "14px",
                lineHeight: "24.05px",
                letterSpacing: "0%",
                color: "#979796",
              }}
            >
              Select Cause
            </Typography>
          </MenuItem>
          {monthlyDonations.map((donation) => (
            <MenuItem
              key={donation.id}
              value={donation.id.toString()}
              sx={
                wrapCauseMenuOptions
                  ? {
                      whiteSpace: "normal",
                      wordBreak: "break-word",
                      alignItems: "flex-start",
                      py: 1.25,
                      minHeight: "auto",
                      width: "100%",
                      maxWidth: "100%",
                      boxSizing: "border-box",
                    }
                  : undefined
              }
            >
              <Typography
                component="div"
                sx={
                  wrapCauseMenuOptions
                    ? causeMenuItemTextSx
                    : {
                        fontWeight: 500,
                        fontSize: "14px",
                        lineHeight: "24.05px",
                        letterSpacing: "0%",
                        color: "#1D1D1B",
                      }
                }
              >
                {getCauseLabel(donation)}
              </Typography>
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Amount Selection Buttons */}
      <Box
        sx={{
          display: "flex",
          gap: styles.buttonBox.gap,
          marginBottom: styles.buttonBox.marginBottom,
          flexWrap: "wrap",
          justifyContent: "space-between",
        }}
      >
        {amountOptions.map((amount) => (
          <Button
            key={amount}
            onClick={() => handleAmountSelect(amount)}
            sx={{
              width: styles.amountButton.width,
              minWidth: styles.amountButton.minWidth,
              height: styles.amountButton.height,
              borderRadius: styles.amountButton.borderRadius,
              paddingTop: styles.amountButton.paddingTop,
              paddingRight: styles.amountButton.paddingRight,
              paddingBottom: styles.amountButton.paddingBottom,
              paddingLeft: styles.amountButton.paddingLeft,
              border: `${styles.amountButton.borderWidth} solid #DDE2E5`,
              backgroundColor:
                selectedAmount === amount ? "#1D1D1B" : "transparent",
              color: selectedAmount === amount ? "#FFFFFF" : "#5E5E5E",
              fontFamily: styles.amountButtonText.fontFamily,
              fontWeight: styles.amountButtonText.fontWeight,
              fontSize: styles.amountButtonText.fontSize,
              lineHeight: styles.amountButtonText.lineHeight,
              letterSpacing: styles.amountButtonText.letterSpacing,
              textTransform: "none",
              "&:hover": {
                backgroundColor:
                  selectedAmount === amount ? "#1D1D1B" : "#f5f5f5",
              },
            }}
          >
            ₹{amount.toLocaleString("en-IN")}
          </Button>
        ))}
      </Box>

      {/* Custom Amount Input */}
      <Box
        sx={{
          height: styles.customAmountInput.height,
          borderRadius: styles.customAmountInput.borderRadius,
          paddingTop: styles.customAmountInput.paddingTop,
          paddingRight: styles.customAmountInput.paddingRight,
          paddingBottom: styles.customAmountInput.paddingBottom,
          paddingLeft: styles.customAmountInput.paddingLeft,
          border: `${styles.customAmountInput.borderWidth} solid #BDD188`,
          background: "#F5F8ED",
          marginBottom: styles.customAmountInput.marginBottom,
          display: "flex",
          alignItems: "center",
          gap: "8px",
        }}
      >
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: styles.customAmountText.fontSize,
            lineHeight: styles.customAmountText.lineHeight,
            letterSpacing: styles.customAmountText.letterSpacing,
            color: "#4A4A49",
            width: styles.currencyIcon.width,
            height: styles.currencyIcon.height,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          ₹
        </Typography>
        <Box
          sx={{ width: "1px", height: "20px", backgroundColor: "#BDD188" }}
        />
        <TextField
          value={customAmount}
          onChange={handleCustomAmountChange}
          placeholder=""
          variant="standard"
          InputProps={{
            disableUnderline: true,
            sx: {
              fontWeight: styles.customAmountText.fontWeight,
              fontSize: styles.customAmountText.fontSize,
              lineHeight: styles.customAmountText.lineHeight,
              letterSpacing: styles.customAmountText.letterSpacing,
              color: "#4A4A49",
              textAlign: "center",
              verticalAlign: "middle",
            },
          }}
          sx={{ flex: 1 }}
        />
      </Box>

      {/* Donate Once Button */}
      <Button
        variant="contained"
        fullWidth
        onClick={handleDonateClick}
        sx={{
          height: styles.donateButton.height,
          borderRadius: styles.donateButton.borderRadius,
          paddingTop: styles.donateButton.paddingTop,
          paddingBottom: styles.donateButton.paddingBottom,
          background: "#F04951",
          boxShadow: styles.donateButton.boxShadow,
          textTransform: "none",
          fontWeight: styles.donateButtonText.fontWeight,
          fontSize: styles.donateButtonText.fontSize,
          lineHeight: styles.donateButtonText.lineHeight,
          letterSpacing: styles.donateButtonText.letterSpacing,
          color: "#FFFFFF",
          textAlign: "center",
          verticalAlign: "middle",
          "&:hover": {
            backgroundColor: "#e03e46",
          },
        }}
      >
        Donate Monthly
      </Button>
    </Box>
  );
};

export default SafeLifeEveryMonth;
